
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete'
import { Dropdown } from 'primereact/dropdown'
import { PersonApi, ShowApi, PersonSearchResultDto, HorseApi, HorseSearchResultDto, ExamApi, ExamDto, ExamParticipantDto, ShowStarterDto, StartersApi } from '../../../api'
import { ShowDto } from '../../../types/ShowDto'
import { authState } from '../../../state/Auth.state'
import { StarterHistoryOverlay } from './StarterHistoryOverlay'

type SortMode = 'place' | 'startingOrder'
interface OverlayTarget { registerId: string; mode: 'horse' | 'rider'; name: string; showId?: number | null; showName?: string | null }

export const History = () => {
  /* STATE – Show */
  const [shows, setShows] = useState<ShowDto[]>([])
  const [selectedShow, setSelectedShow] = useState<ShowDto | null>(null)
  const [showsLoading, setShowsLoading] = useState(false)

  /* STATE – Exams */
  const [exams, setExams] = useState<ExamDto[]>([])
  const [selectedExam, setSelectedExam] = useState<ExamDto | null>(null)
  const [examsLoading, setExamsLoading] = useState(false)

  /* STATE – Teilnehmer */
  const [participants, setParticipants] = useState<ExamParticipantDto[]>([])
  const [participantsLoading, setParticipantsLoading] = useState(false)
  const [sortMode, setSortMode] = useState<SortMode>('startingOrder')
  const [overlay, setOverlay] = useState<OverlayTarget | null>(null)

  const openOverlay = useCallback((target: OverlayTarget) => {
    window.history.pushState(null, '', window.location.href)
    setOverlay(target)
  }, [])

  const closeOverlay = useCallback(() => {
    setOverlay(null)
  }, [])

  useEffect(() => {
    if (!overlay) return
    const handler = () => setOverlay(null)
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [overlay])

  /* STATE – Alle Starter (wenn keine Prüfung gewählt) */
  const [allStarters, setAllStarters] = useState<ShowStarterDto[]>([])
  const [allStartersLoading, setAllStartersLoading] = useState(false)

  /* STATE – Person */
  const [personQuery, setPersonQuery] = useState('')
  const [personSuggestions, setPersonSuggestions] = useState<PersonSearchResultDto[]>([])
  const [selectedPerson, setSelectedPerson] = useState<PersonSearchResultDto | null>(null)
  const personTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* STATE – Pferd */
  const [horseQuery, setHorseQuery] = useState('')
  const [horseSuggestions, setHorseSuggestions] = useState<HorseSearchResultDto[]>([])
  const [selectedHorse, setSelectedHorse] = useState<HorseSearchResultDto | null>(null)
  const horseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* LOGIC – alle Shows laden */
  useEffect(() => {
    setShowsLoading(true)
    new ShowApi(authState.apicfg())
      .apiShowGetRaw()
      .then(r => r.raw.json())
      .then((shows: ShowDto[]) => setShows([...shows].sort((a, b) => {
        const da = a.startDate ? new Date(a.startDate).getTime() : 0
        const db = b.startDate ? new Date(b.startDate).getTime() : 0
        return db - da
      })))
      .catch(() => setShows([]))
      .finally(() => setShowsLoading(false))
  }, [])

  /* LOGIC – Teilnehmer laden wenn Prüfung gewählt */
  const loadParticipants = useCallback((exam: ExamDto | null) => {
    setParticipants([])
    setSortMode('startingOrder')
    if (exam?.id == null) return
    setParticipantsLoading(true)
    new ExamApi(authState.apicfg())
      .apiExamExamIdDetailsGet({ examId: exam.id })
      .then(data => setParticipants(data))
      .catch(() => setParticipants([]))
      .finally(() => setParticipantsLoading(false))
  }, [])

  /* LOGIC – Person search */
  const searchPersons = useCallback((e: AutoCompleteCompleteEvent) => {
    const q = e.query?.trim()
    if (personTimer.current) clearTimeout(personTimer.current)
    if (!q || q.length < 2) { setPersonSuggestions([]); return }
    personTimer.current = setTimeout(() => {
      new PersonApi(authState.apicfg())
        .apiPersonSearchGet({ q })
        .then(r => setPersonSuggestions(r.items ?? []))
        .catch(() => setPersonSuggestions([]))
    }, 300)
  }, [])

  /* LOGIC – Horse search */
  const searchHorses = useCallback((e: AutoCompleteCompleteEvent) => {
    const q = e.query?.trim()
    if (horseTimer.current) clearTimeout(horseTimer.current)
    if (!q || q.length < 2) { setHorseSuggestions([]); return }
    horseTimer.current = setTimeout(() => {
      new HorseApi(authState.apicfg())
        .apiHorseSearchGet({ q })
        .then(r => setHorseSuggestions(r.items ?? []))
        .catch(() => setHorseSuggestions([]))
    }, 300)
  }, [])

  /* DERIVED – sortierte Teilnehmer (place=0 ausgeblendet) */
  const sortedParticipants = useMemo(() =>
    [...participants]
      .filter(p => sortMode === 'place' ? (p.place ?? 0) !== 0 : true)
      .sort((a, b) => {
        if (sortMode === 'place') {
          const pa = a.place ?? 9999
          const pb = b.place ?? 9999
          return pa !== pb ? pa - pb : (a.startNo ?? 0) - (b.startNo ?? 0)
        }
        return (a.startingOrder ?? a.startNo ?? 0) - (b.startingOrder ?? b.startNo ?? 0)
      })
  , [participants, sortMode])

  const startingOrderUndefined = useMemo(() =>
    sortMode === 'startingOrder' && participants.length > 0 &&
    participants.every(p => (p.startingOrder ?? 0) === 0)
  , [participants, sortMode])

  const placeUndefined = useMemo(() =>
    participants.length > 0 && participants.every(p => (p.place ?? 0) === 0)
  , [participants])

  /* LOGIC – aus Overlay zu einer Prüfung navigieren */
  const navigateToExam = useCallback((showId: number, examId: number) => {
    setOverlay(null)
    const show = shows.find(s => s.id === showId) ?? null
    setSelectedShow(show)
    setSelectedExam(null)
    setParticipants([])
    setAllStarters([])
    if (show?.id == null) return
    setExamsLoading(true)
    setAllStartersLoading(true)
    Promise.all([
      new ExamApi(authState.apicfg())
        .apiExamByShowShowIdGet({ showId: show.id })
        .then(data => {
          const sorted = [...data].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
          setExams(sorted)
          const target = sorted.find(e => e.id === examId) ?? null
          setSelectedExam(target)
          loadParticipants(target)
        })
        .catch(() => setExams([]))
        .finally(() => setExamsLoading(false)),
      new StartersApi(authState.apicfg())
        .apiStartersShowShowIdStarterGet({ showId: show.id })
        .then(data => setAllStarters([...data].sort((a, b) => (a.startNo ?? 0) - (b.startNo ?? 0))))
        .catch(() => setAllStarters([]))
        .finally(() => setAllStartersLoading(false)),
    ])
  }, [shows, loadParticipants])

  /* TEMPLATE */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', maxWidth: '100%' }}>

      {/* Turnier */}
      <div>
        <div style={{ fontSize: '0.88rem', color: '#555', marginBottom: '0.4rem' }}>
          Bitte wählen Sie das Turnier, zu dem Sie weitere Informationen erhalten möchten.
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Dropdown
            value={selectedShow}
            options={shows}
            optionLabel='name'
            placeholder={showsLoading ? 'Wird geladen \u2026' : 'Turnier ausw\u00e4hlen'}
            filter
            filterBy='name'
            onChange={(e) => {
              const show: ShowDto = e.value
              setSelectedShow(show)
              setSelectedExam(null)
              setParticipants([])
              setExams([])
              setAllStarters([])
              if (show?.id != null) {
                setExamsLoading(true)
                new ExamApi(authState.apicfg())
                  .apiExamByShowShowIdGet({ showId: show.id })
                  .then(data => setExams([...data].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))))
                  .catch(() => setExams([]))
                  .finally(() => setExamsLoading(false))
                setAllStartersLoading(true)
                new StartersApi(authState.apicfg())
                  .apiStartersShowShowIdStarterGet({ showId: show.id })
                  .then(data => setAllStarters([...data].sort((a, b) => (a.startNo ?? 0) - (b.startNo ?? 0))))
                  .catch(() => setAllStarters([]))
                  .finally(() => setAllStartersLoading(false))
              }
            }}
            style={{ flex: 1 }}
            scrollHeight='380px'
            itemTemplate={(show: ShowDto) => (
              <span>{show.name}{show.startDate ? ` (${new Date(show.startDate).getFullYear()})` : ''}{show.city ? ` \u2013 ${show.city}` : ''}</span>
            )}
            disabled={showsLoading}
          />
          {selectedShow && (
            <button
              onClick={() => { setSelectedShow(null); setSelectedExam(null); setParticipants([]); setExams([]); setAllStarters([]) }}
              title='Turnier leeren'
              style={{ flexShrink: 0, background: 'none', border: '1px solid #ccc',
                       borderRadius: '4px', cursor: 'pointer', fontSize: '1rem',
                       padding: '0.2rem 0.5rem', lineHeight: 1, color: '#666' }}
            >
              ✕
            </button>
          )}
        </div>
        {selectedShow && (
          <>
            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
              {selectedShow.startDate && <span>{new Date(selectedShow.startDate).toLocaleDateString('de-DE')}</span>}
              {selectedShow.startDate && selectedShow.endDate && <span> &ndash; </span>}
              {selectedShow.endDate && <span>{new Date(selectedShow.endDate).toLocaleDateString('de-DE')}</span>}
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.5rem' }}>
              <Dropdown
                value={selectedExam}
                options={exams}
                optionLabel='name'
                onChange={(e) => {
                  setSelectedExam(e.value ?? null)
                  loadParticipants(e.value ?? null)
                }}
                placeholder={examsLoading ? 'Wird geladen \u2026' : 'Pr\u00fcfung ausw\u00e4hlen'}
                filter
                filterBy='name'
                emptyMessage='Keine Pr\u00fcfungen gefunden'
                emptyFilterMessage='Keine Treffer'
                disabled={examsLoading}
                style={{ flex: 1, minWidth: 0 }}
                scrollHeight='380px'
                itemTemplate={(exam: ExamDto) => <span>{exam.name}{exam.type ? ` [${exam.type}]` : ''}</span>}
                valueTemplate={(exam: ExamDto) => exam ? <span>{exam.name}{exam.type ? ` [${exam.type}]` : ''}</span> : <span>{examsLoading ? 'Wird geladen \u2026' : 'Pr\u00fcfung ausw\u00e4hlen'}</span>}
              />
              {selectedExam && (
                <button
                  onClick={() => { setSelectedExam(null); setParticipants([]) }}
                  title='Pr\u00fcfung leeren'
                  style={{ flexShrink: 0, background: 'none', border: '1px solid #ccc',
                           borderRadius: '4px', cursor: 'pointer', fontSize: '1rem',
                           padding: '0.2rem 0.5rem', lineHeight: 1, color: '#666' }}
                >
                  ✕
                </button>
              )}
            </div>
            {/* Prüfungsbeschreibung */}
            {selectedExam?.description && (
              <div style={{ marginTop: '0.35rem', fontSize: '0.82rem', color: '#555',
                           whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {selectedExam.description}
              </div>
            )}
          </>
        )}
      </div>

      {/* Alle Starter wenn keine Prüfung gewählt */}
      {selectedShow && !selectedExam && (
        <div>
          {allStartersLoading && <div style={{ color: '#888' }}>Wird geladen &#8230;</div>}
          {!allStartersLoading && allStarters.length > 0 && (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: '360px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
                    <th style={{ padding: '0.2rem 0.3rem' }}>St.Nr.</th>
                    <th style={{ padding: '0.2rem 0.3rem' }}>Pferd</th>
                    <th style={{ padding: '0.2rem 0.3rem' }}>Reiter</th>
                    <th style={{ padding: '0.2rem 0.3rem' }}>Prüfungen</th>
                  </tr>
                </thead>
                <tbody>
                  {allStarters.map((s, idx) => (
                    <tr key={idx} style={{ borderTop: idx > 0 ? '1px solid #eee' : undefined }}>
                      <td style={{ padding: '0.25rem 0.3rem' }}>{s.startNo ?? '\u2013'}</td>
                      <td style={{ padding: '0.25rem 0.3rem' }}>
                        {s.horseRegisterId
                          ? <a href='#' onClick={(e) => { e.preventDefault(); openOverlay({ registerId: s.horseRegisterId!, mode: 'horse', name: s.horseName ?? s.horseRegisterId!, showId: selectedShow?.id, showName: selectedShow?.name }) }}>
                              {s.horseName ?? '\u2013'}
                            </a>
                          : (s.horseName ?? '\u2013')}
                      </td>
                      <td style={{ padding: '0.25rem 0.3rem' }}>
                        {s.riderRegisterId
                          ? <a href='#' onClick={(e) => { e.preventDefault(); const n = [s.riderFirstName, s.riderLastName].filter(Boolean).join(' '); openOverlay({ registerId: s.riderRegisterId!, mode: 'rider', name: n || s.riderRegisterId!, showId: selectedShow?.id, showName: selectedShow?.name }) }}>
                              {[s.riderFirstName, s.riderLastName].filter(Boolean).join(' ') || '\u2013'}
                            </a>
                          : ([s.riderFirstName, s.riderLastName].filter(Boolean).join(' ') || '\u2013')}
                      </td>
                      <td style={{ padding: '0.25rem 0.3rem' }}>
                        {(s.exams ?? []).map((ex, ei) => (
                          <span
                            key={ei}
                            onClick={() => { const exam = exams.find(e => e.id === ex.examId); if (exam) { setSelectedExam(exam); loadParticipants(exam) } }}
                            style={{ display: 'inline-block', marginRight: '0.3rem', marginBottom: '0.2rem',
                                     padding: '0.1rem 0.4rem', border: '1px solid #bbb', borderRadius: '3px',
                                     fontSize: '0.78rem', cursor: 'pointer', background: '#f5f5f5' }}
                            title={ex.examName ?? undefined}
                          >
                            {ex.examName ?? ex.examId}{ex.type ? ` [${ex.type}]` : ''}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!allStartersLoading && allStarters.length === 0 && (
            <div style={{ color: '#888', fontSize: '0.88rem' }}>Keine Starter gefunden.</div>
          )}
        </div>
      )}

      {/* Teilnehmerliste */}
      {selectedExam && (
        <div>
          {participantsLoading && <div style={{ color: '#888' }}>Wird geladen &#8230;</div>}

          {!participantsLoading && sortedParticipants.length > 0 && (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: '300px' }}>
              <colgroup>
                <col style={{ width: '2.5rem' }} />
                <col style={{ width: '4rem' }} />
                <col />
              </colgroup>
              <thead>
                <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
                  <th style={{ padding: '0.2rem 0.4rem' }}>
                    <a
                      href='#'
                      onClick={(e) => {
                        e.preventDefault()
                        const toPlace = sortMode === 'startingOrder'
                        if (toPlace && placeUndefined) return
                        if (!toPlace && startingOrderUndefined) return
                        setSortMode(m => m === 'place' ? 'startingOrder' : 'place')
                      }}
                      title={sortMode === 'place' ? 'Sortiert nach Platzierung \u2013 klicken f\u00fcr Startfolge' : 'Klicken f\u00fcr Sortierung nach Platzierung'}
                      style={{ textDecoration: 'none', whiteSpace: 'nowrap',
                               color: (sortMode === 'startingOrder' && startingOrderUndefined) || (sortMode === 'place' && placeUndefined) ? '#bbb' : 'inherit',
                               cursor: (sortMode === 'startingOrder' && startingOrderUndefined) || (sortMode === 'place' && placeUndefined) ? 'default' : 'pointer' }}
                    >
                      Pl.{sortMode === 'place' ? ' \u25b2' : ' \u25bd'}
                    </a>
                  </th>
                  <th style={{ padding: '0.2rem 0.3rem' }}>
                    <div>St.Nr.</div>
                    <div style={{ fontWeight: 400, fontSize: '0.78rem', color: '#888' }}>Rasse</div>
                  </th>
                  <th style={{ padding: '0.2rem 0.3rem' }}>
                    <div>Pferd &nbsp;<span style={{ fontWeight: 400, color: '#888' }}>Reiter</span></div>
                    <div style={{ fontWeight: 400, fontSize: '0.78rem', color: '#888' }}>Besitzer &nbsp; Züchter</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedParticipants.map((p, idx) => {
                  const hasScores = (p.place ?? 0) !== 0 && p.scores && p.scores.length > 0
                  const hasResult = (p.place ?? 0) !== 0 && !!p.result && parseFloat(String(p.result).replace(',', '.')) !== 0
                  const scores = (() => {
                    if (!hasScores) return []
                    const arr = [...p.scores!].filter(s => s !== 0)
                    while (arr.length > 0 && arr[arr.length - 1] === 0) arr.pop()
                    return arr
                  })()
                  const span = (hasScores || hasResult) ? 3 : 2
                  const riderName = [p.riderFirstName, p.riderLastName].filter(Boolean).join(' ')
                  return (
                  <React.Fragment key={p.startNo ?? idx}>
                    {/* Zeile 1: Platz / St.Nr. / Pferd + Reiter */}
                    <tr style={{ borderTop: idx > 0 ? '1px solid #e8e8e8' : undefined }}>
                      <td rowSpan={span} style={{ padding: '0.2rem 0.3rem', verticalAlign: 'top', fontWeight: 600 }}>
                        {p.place != null ? p.place : '\u2013'}
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem', verticalAlign: 'top' }}>
                        {p.startNo ?? '\u2013'}
                      </td>
                      <td style={{ padding: '0.2rem 0.3rem' }}>
                        <span style={{ fontWeight: 500 }}>
                          {p.horseRegisterId
                            ? <a href='#' onClick={(e) => { e.preventDefault(); openOverlay({ registerId: p.horseRegisterId!, mode: 'horse', name: p.horseName ?? p.horseRegisterId!, showId: selectedShow?.id, showName: selectedShow?.name }) }}>
                                {p.horseName ?? '\u2013'}
                              </a>
                            : (p.horseName ?? '\u2013')}
                        </span>
                        {riderName && (
                          <span style={{ marginLeft: '0.6rem', color: '#555' }}>
                            {p.riderRegisterId
                              ? <a href='#' onClick={(e) => { e.preventDefault(); openOverlay({ registerId: p.riderRegisterId!, mode: 'rider', name: riderName || p.riderRegisterId!, showId: selectedShow?.id, showName: selectedShow?.name }) }}>
                                  {riderName}
                                </a>
                              : riderName}
                          </span>
                        )}
                      </td>
                    </tr>
                    {/* Zeile 2: Rasse | Besitzer + Züchter */}
                    <tr>
                      <td style={{ padding: '0.05rem 0.3rem 0.3rem', color: '#555', fontSize: '0.82rem' }}>
                        {p.horseBreedKz && (
                          <span
                            title={p.horseBreed ?? undefined}
                            style={{ cursor: p.horseBreed ? 'help' : undefined,
                                     borderBottom: p.horseBreed ? '1px dotted #aaa' : undefined }}
                          >
                            {p.horseBreedKz}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.05rem 0.3rem 0.3rem', color: '#555', fontSize: '0.82rem' }}>
                        {p.horseOwner && <span>{p.horseOwner}</span>}
                        {p.horseOwner && p.horseBreeder && <span style={{ margin: '0 0.3rem' }}>&middot;</span>}
                        {p.horseBreeder && <span>{p.horseBreeder}</span>}
                      </td>
                    </tr>
                    {/* Zeile 3: Result | Scores */}
                    {(hasScores || hasResult) && (
                      <tr>
                        <td style={{ padding: '0.05rem 0.3rem 0.35rem', color: '#444', fontSize: '0.82rem', fontWeight: 600 }}>
                          {hasResult && parseFloat(String(p.result!).replace(',', '.')).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '0.05rem 0.3rem 0.35rem', color: '#444', fontSize: '0.82rem' }}>
                          {scores.map((s, si) => (
                            <span key={si} style={{ marginRight: '0.4rem' }}>{Number(s).toFixed(2)}</span>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  )
                })}
              </tbody>
            </table>
            </div>
          )}

          {!participantsLoading && sortedParticipants.length === 0 && (
            <div style={{ color: '#888', fontSize: '0.88rem' }}>Keine Teilnehmer gefunden.</div>
          )}
        </div>
      )}

      {/* Person / Reiter */}
      {!selectedShow && (
        <div>
          <div style={{ fontSize: '0.88rem', color: '#555', marginBottom: '0.4rem' }}>
            Hier können Sie in allen IGV registrierten Reitern suchen.
          </div>
          <AutoComplete
            value={selectedPerson ?? personQuery}
            suggestions={personSuggestions}
            completeMethod={searchPersons}
            field='lastName'
            placeholder='Reiter suchen'
            onChange={(e) => {
              if (typeof e.value === 'string') { setPersonQuery(e.value); setSelectedPerson(null) }
              else {
                setSelectedPerson(e.value)
                if (e.value?.registerId) openOverlay({ registerId: e.value.registerId, mode: 'rider', name: [e.value.lastName, e.value.firstName].filter(Boolean).join(', ') || e.value.registerId })
              }
            }}
            style={{ width: '100%' }}
            scrollHeight='380px'
            itemTemplate={(person: PersonSearchResultDto) => (
              <span>{person.lastName}, {person.firstName}</span>
            )}
          />
        </div>
      )}

      {/* Pferd */}
      {!selectedShow && (
        <div>
          <div style={{ fontSize: '0.88rem', color: '#555', marginBottom: '0.4rem' }}>
            Hier können Sie in allen IGV registrierten Pferden suchen.
          </div>
          <AutoComplete
            value={selectedHorse ?? horseQuery}
            suggestions={horseSuggestions}
            completeMethod={searchHorses}
            field='name'
            placeholder='Pferd suchen'
            onChange={(e) => {
              if (typeof e.value === 'string') { setHorseQuery(e.value); setSelectedHorse(null) }
              else {
                setSelectedHorse(e.value)
                if (e.value?.registerId) openOverlay({ registerId: e.value.registerId, mode: 'horse', name: e.value.name ?? e.value.registerId })
              }
            }}
            style={{ width: '100%' }}
            scrollHeight='380px'
            itemTemplate={(horse: HorseSearchResultDto) => (
              <span>{horse.name}{horse.breed ? ` (${horse.breed})` : ''}</span>
            )}
          />
        </div>
      )}

      {overlay && (
        <StarterHistoryOverlay
          registerId={overlay!.registerId}
          mode={overlay!.mode}
          name={overlay!.name}
          showId={overlay!.showId}
          showName={overlay!.showName}
          onHide={closeOverlay}
          onNavigateToExam={navigateToExam}
          onOpenOther={(regId: string, m: 'horse' | 'rider', n: string) => openOverlay({ registerId: regId, mode: m, name: n })}/>
      )}
    </div>
  )
}

